import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import StatNumber from "@/components/StatNumber/StatNumber";
import { getCase, localizeCase } from "@/data/caseStudies";
import { CASE_PREFIX, SOLUTION_PREFIX } from "@/i18n/config";
import { useLocale } from "@/i18n/useLocale";

export interface ProjectDevice {
  src: string;
  alt: string;
  width: number;
  height: number;
}

interface ProjectPanelProps {
  variant: "ecommerce" | "software";
  /** Si las capturas se montan. Un slide que todavia no se ve las difiere (ver HeroSlides). */
  showImages: boolean;
  /** Cuantas veces se activo el slide: remonta las cifras para que cuenten de nuevo. */
  run: number;
  kicker: string;
  /** Etiqueta chica arriba a la derecha (solo en el slide de software). */
  pill?: string;
  main: ProjectDevice;
  side: ProjectDevice;
  /** Slugs de los casos cuyo primer numero se muestra. */
  caseSlugs: string[];
  /** Solucion (src/data/solutions.ts) a la que lleva el slide. */
  solutionSlug: string;
}

/** "+50%" -> { prefix: "+", value: 50, suffix: "%" }; "2 hs" -> { "", 2, " hs" }. */
function parseStat(raw: string) {
  const match = /^([+\-−]?)(\d+)(.*)$/.exec(raw.trim());
  return match ? { prefix: match[1], value: Number(match[2]), suffix: match[3] } : null;
}

function Kpi({ slug, order, run }: { slug: string; order: number; run: number }) {
  const { t, locale } = useLocale();
  const raw = getCase(slug);
  const study = raw ? localizeCase(raw, locale) : undefined;
  const stat = study?.stats?.[0];

  if (!study || !stat) return null;

  const parsed = parseStat(stat.value);

  return (
    <Link
      to={`${CASE_PREFIX[locale]}/${study.slug}`}
      className="hs-kpi hs-enter hs-enter--blur"
      style={{ "--i": 3 + order } as CSSProperties}
    >
      <span className="hs-kpi__value">
        {parsed ? (
          <StatNumber
            key={run}
            value={parsed.value}
            prefix={parsed.prefix}
            suffix={parsed.suffix}
            duration={1500}
            delay={550 + order * 140}
            animateOnMount
          />
        ) : (
          stat.value
        )}
      </span>
      <span className="hs-kpi__label">{stat.label}</span>
      <span className="hs-kpi__source">
        {study.client} · {t.hero.stage.viewCase} →
      </span>
    </Link>
  );
}

/**
 * Slides 2 y 3: capacidad a la vista. Capturas reales de proyectos entregados
 * -tiendas online en uno, sistemas a medida en el otro- flotando sobre el
 * escenario, y debajo los resultados medibles de dos casos de verdad, con
 * link a cada uno. Nada de esto es ilustracion generica: los numeros, los
 * nombres y las imagenes salen de los propios casos del sitio.
 */
function ProjectPanel({ variant, showImages, run, kicker, pill, main, side, caseSlugs, solutionSlug }: ProjectPanelProps) {
  const { t, locale } = useLocale();

  return (
    <div className={`hs-project hs-project--${variant}`}>
      <div className="hs-project__head">
        <p className="hs-project__kicker hs-enter" style={{ "--i": 0 } as CSSProperties}>
          {kicker}
        </p>

        {pill && (
          <p className="hs-project__pill hs-enter" style={{ "--i": 1 } as CSSProperties}>
            {pill}
          </p>
        )}
      </div>

      <div className="hs-project__devices">
        <div className="hs-device hs-device--main" style={{ "--depth": "10px" } as CSSProperties}>
          {showImages && (
            <img
              className="hs-enter hs-float"
              style={{ "--i": 1 } as CSSProperties}
              src={main.src}
              alt={main.alt}
              width={main.width}
              height={main.height}
              loading="lazy"
              decoding="async"
            />
          )}
        </div>
        <div className="hs-device hs-device--side" style={{ "--depth": "20px" } as CSSProperties}>
          {showImages && (
            <img
              className="hs-enter hs-float hs-float--alt"
              style={{ "--i": 2 } as CSSProperties}
              src={side.src}
              alt={side.alt}
              width={side.width}
              height={side.height}
              loading="lazy"
              decoding="async"
            />
          )}
        </div>
      </div>

      <div className="hs-project__kpis">
        {caseSlugs.map((slug, order) => (
          <Kpi key={slug} slug={slug} order={order} run={run} />
        ))}
      </div>

      <Link
        to={`${SOLUTION_PREFIX[locale]}/${solutionSlug}`}
        className="hs-more hs-enter"
        style={{ "--i": 5 } as CSSProperties}
      >
        {t.hero.stage.viewSolution} →
      </Link>
    </div>
  );
}

export default ProjectPanel;
